import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../api/client';
import {
  TEXT_DARK, TEXT_SECONDARY, TEXT_MUTED,
  FONT_HEADING, PRIMARY, BG_SURFACE, BORDER_LIGHT,
} from './designTokens';

interface SeatingProposal {
  guestId: string;
  guestName: string;
  seatId: string;
  seatCompositeId: string;
  tableNumber: number;
  tableName: string;
  zoneName: string;
  reason: string;
  warnings: string[];
}

interface EngineResult {
  proposals: SeatingProposal[];
  unplacedGuests: { id: string; name: string; reason: string }[];
  summary: {
    totalUnassigned: number;
    totalPlaced: number;
    totalUnplaced: number;
  };
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '800px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  runBtn: {
    padding: '0.55rem 1.2rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  summary: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: '1.1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
  },
  summaryLabel: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  acceptAllBtn: {
    padding: '0.45rem 0.9rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  rejectAllBtn: {
    padding: '0.45rem 0.9rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  proposalList: {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '1rem',
  },
  proposalItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0.85rem',
    borderBottom: '1px solid #f0f0f0',
  },
  proposalInfo: {
    flex: 1,
  },
  proposalGuest: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  proposalSeat: {
    fontSize: '0.75rem',
    color: TEXT_SECONDARY,
    marginTop: '0.1rem',
  },
  proposalReason: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
    marginTop: '0.1rem',
  },
  warningBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#e65100',
    backgroundColor: '#fff3e0',
    padding: '0.1rem 0.35rem',
    borderRadius: '3px',
    marginLeft: '0.35rem',
  },
  proposalActions: {
    display: 'flex',
    gap: '0.35rem',
  },
  acceptBtn: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: '#fdecea',
    color: '#d32f2f',
    border: '1px solid #ef9a9a',
    borderRadius: '3px',
    cursor: 'pointer',
  },
  acceptedBadge: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#2e7d32',
    padding: '0.2rem 0.5rem',
  },
  rejectedBadge: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#d32f2f',
    padding: '0.2rem 0.5rem',
  },
  unplacedSection: {
    marginTop: '1rem',
  },
  unplacedTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#d32f2f',
    marginBottom: '0.5rem',
  },
  unplacedItem: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.82rem',
    color: '#555',
    borderBottom: '1px solid #f0f0f0',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.9rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  success: {
    padding: '0.5rem 0.75rem',
    color: '#2e7d32',
    fontSize: '0.8rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: '#888',
    fontSize: '0.9rem',
    backgroundColor: '#fafafa',
    borderRadius: '6px',
    border: '1px dashed #ddd',
  },
};

export function SeatingEnginePanel() {
  const navigate = useNavigate();
  const [result, setResult] = useState<EngineResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [decisions, setDecisions] = useState<Record<string, 'accepted' | 'rejected'>>({});

  const runEngine = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setDecisions({});
    try {
      const data = await apiClient<EngineResult>('/table-management/seating-engine/run', {
        method: 'POST',
      });
      setResult(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to run seating engine.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (proposal: SeatingProposal) => {
    try {
      await apiClient(`/table-management/seats/${proposal.seatId}/assign`, {
        method: 'POST',
        body: { guestId: proposal.guestId },
      });
      setDecisions((prev) => ({ ...prev, [proposal.guestId]: 'accepted' }));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || `Failed to assign ${proposal.guestName}.`);
    }
  };

  const handleReject = (proposal: SeatingProposal) => {
    setDecisions((prev) => ({ ...prev, [proposal.guestId]: 'rejected' }));
  };

  const handleAcceptAll = async () => {
    if (!result) return;
    setError('');
    const pending = result.proposals.filter((p) => !decisions[p.guestId]);
    let successCount = 0;
    let failCount = 0;

    for (const proposal of pending) {
      try {
        await apiClient(`/table-management/seats/${proposal.seatId}/assign`, {
          method: 'POST',
          body: { guestId: proposal.guestId },
        });
        setDecisions((prev) => ({ ...prev, [proposal.guestId]: 'accepted' }));
        successCount++;
      } catch {
        failCount++;
        setDecisions((prev) => ({ ...prev, [proposal.guestId]: 'rejected' }));
      }
    }

    setSuccess(`Accepted ${successCount} assignments${failCount > 0 ? `, ${failCount} failed` : ''}.`);
  };

  const handleRejectAll = () => {
    if (!result) return;
    const newDecisions: Record<string, 'accepted' | 'rejected'> = { ...decisions };
    result.proposals.forEach((p) => {
      if (!newDecisions[p.guestId]) {
        newDecisions[p.guestId] = 'rejected';
      }
    });
    setDecisions(newDecisions);
  };

  const pendingCount = result
    ? result.proposals.filter((p) => !decisions[p.guestId]).length
    : 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Seating Engine</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => navigate('/seating-engine')}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#555', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            🧠 AI Simulator
          </button>
          <button
            onClick={() => navigate('/seating-engine?tab=saved-runs')}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            📋 Saved Runs
          </button>
          <button
            style={{
              ...styles.runBtn,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onClick={runEngine}
            disabled={loading}
            aria-label="Run seating engine"
          >
            {loading ? 'Running...' : 'Run Seating Engine'}
          </button>
        </div>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {success && <div style={styles.success} role="status">{success}</div>}

      {loading && <div style={styles.loading}>Running seating engine... This may take a moment.</div>}

      {!loading && !result && (
        <div style={styles.empty}>
          <p>Click "Run Seating Engine" to generate seating suggestions for unassigned guests.</p>
          <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
            The engine applies rules for family grouping, chapter affiliation, VIP placement, accessibility, and more.
          </p>
        </div>
      )}

      {result && (
        <>
          <div style={styles.summary} role="region" aria-label="Engine results summary">
            <div style={styles.summaryItem}>
              <span style={styles.summaryValue}>{result.summary.totalUnassigned}</span>
              <span style={styles.summaryLabel}>Unassigned</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryValue, color: '#2e7d32' }}>{result.summary.totalPlaced}</span>
              <span style={styles.summaryLabel}>Placed</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryValue, color: '#d32f2f' }}>{result.summary.totalUnplaced}</span>
              <span style={styles.summaryLabel}>Unplaced</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={{ ...styles.summaryValue, color: '#1565c0' }}>{pendingCount}</span>
              <span style={styles.summaryLabel}>Pending</span>
            </div>
          </div>

          {pendingCount > 0 && (
            <div style={styles.actions}>
              <button
                style={styles.acceptAllBtn}
                onClick={handleAcceptAll}
                aria-label="Accept all pending suggestions"
              >
                Accept All ({pendingCount})
              </button>
              <button
                style={styles.rejectAllBtn}
                onClick={handleRejectAll}
                aria-label="Reject all pending suggestions"
              >
                Reject All
              </button>
            </div>
          )}

          {result.proposals.length > 0 && (
            <div style={styles.proposalList} role="list" aria-label="Seating proposals">
              {result.proposals.map((proposal) => {
                const decision = decisions[proposal.guestId];
                return (
                  <div
                    key={proposal.guestId}
                    style={{
                      ...styles.proposalItem,
                      backgroundColor: decision === 'accepted'
                        ? '#f1f8e9'
                        : decision === 'rejected'
                        ? '#fce4ec'
                        : '#fff',
                      opacity: decision ? 0.8 : 1,
                    }}
                    role="listitem"
                  >
                    <div style={styles.proposalInfo}>
                      <div style={styles.proposalGuest}>
                        {proposal.guestName}
                        {proposal.warnings.map((w, i) => (
                          <span key={i} style={styles.warningBadge} title={w}>
                            ⚠ {w}
                          </span>
                        ))}
                      </div>
                      <div style={styles.proposalSeat}>
                        → {proposal.seatCompositeId} ({proposal.tableName}, {proposal.zoneName})
                      </div>
                      <div style={styles.proposalReason}>{proposal.reason}</div>
                    </div>

                    <div style={styles.proposalActions}>
                      {!decision && (
                        <>
                          <button
                            style={styles.acceptBtn}
                            onClick={() => handleAccept(proposal)}
                            aria-label={`Accept assignment for ${proposal.guestName}`}
                          >
                            ✓ Accept
                          </button>
                          <button
                            style={styles.rejectBtn}
                            onClick={() => handleReject(proposal)}
                            aria-label={`Reject assignment for ${proposal.guestName}`}
                          >
                            ✗ Reject
                          </button>
                        </>
                      )}
                      {decision === 'accepted' && (
                        <span style={styles.acceptedBadge}>✓ Accepted</span>
                      )}
                      {decision === 'rejected' && (
                        <span style={styles.rejectedBadge}>✗ Rejected</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {result.unplacedGuests.length > 0 && (
            <div style={styles.unplacedSection}>
              <div style={styles.unplacedTitle}>
                Unplaced Guests ({result.unplacedGuests.length})
              </div>
              <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                {result.unplacedGuests.map((guest) => (
                  <div key={guest.id} style={styles.unplacedItem}>
                    <strong>{guest.name}</strong> — {guest.reason}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
